import { useEffect } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { CloseButton } from '../shared/ui';
import { activeMenuPath, isExternal, type MenuSection } from './menu';

/**
 * The menu under a finger.
 *
 * The bar itself does not fit on a phone, and un-hiding it there — which is
 * what the hamburger used to do — put eight wrapping pills across three lines,
 * each of them still opening a 13rem popover anchored under wherever its pill
 * had landed. Two taps to reach any screen, half of them off the side of a
 * 375px window, and the popover covering the pills you would have to go back
 * to. Nothing about that arrangement survives being narrow, so below `lg` the
 * menu is a different thing rather than the same thing squeezed.
 *
 * A drawer, then: one section per row, opened in place. `Dialog` brings the
 * scroll lock, the focus trap and Escape with it, and being a drawer rather
 * than a popover means the panel is never anchored to anything that could
 * push it off screen.
 *
 * Every row is 44px, which is the target a finger can actually hit and the
 * size the rest of the app reaches for with `touch:py-3`. It is written plain
 * here because this whole component is already behind a width — a mouse never
 * sees it, so there is no desktop rendering to protect.
 */

const SECTION_BUTTON =
  'group flex w-full cursor-pointer items-center justify-between gap-2 border-0 bg-transparent ' +
  'px-4 py-3 text-left text-sm font-semibold text-hilevel-slate data-[open]:bg-hilevel-offwhite';

/** Items sit under their section head, indented, with room for the marker. */
const ITEM = 'flex items-center border-l-2 py-3 pl-8 pr-4 text-sm text-hilevel-slate no-underline';

/** Home is a section head's sibling, not an item, so it is not indented. */
const HOME = 'flex items-center border-l-2 py-3 pl-4 pr-4 text-sm text-hilevel-slate no-underline';

/**
 * The page you are on: a red edge, because an indented row cannot say it by
 * moving.
 *
 * The edge is a separate word from the rows above rather than an override on
 * them, because the two would be the same property and the class strings do
 * not decide that — the stylesheet's own order does, and `border-transparent`
 * lands after `border-hilevel-red` in it. Written as an override, the marker
 * never appeared at all.
 */
const MARKED = 'border-hilevel-red bg-hilevel-light font-semibold';
const UNMARKED = 'border-transparent';

export function MobileMenu({
  open,
  onClose,
  sections,
  onPrefetchRoute,
}: {
  open: boolean;
  onClose: () => void;
  sections: MenuSection[];
  onPrefetchRoute?: (path: string) => void;
}) {
  const { pathname } = useLocation();
  const active = activeMenuPath(sections, pathname);

  // Closing on the route rather than on the click covers everything that can
  // move the page: the items here, the browser's own back button, and a
  // redirect the destination decides on once it has loaded.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the pathname is the signal
  }, [pathname]);

  // A tablet turned to landscape crosses into `lg`, where the real menu bar
  // is drawn and the hamburger is gone. Hiding the drawer in CSS would leave
  // it holding the focus trap and the scroll lock with nothing on screen to
  // explain why, so it is closed rather than hidden.
  useEffect(() => {
    if (!open) return;
    const wide = window.matchMedia('(min-width: 1024px)');
    if (wide.matches) {
      onClose();
      return;
    }
    const handleChange = () => {
      if (wide.matches) onClose();
    };
    wide.addEventListener('change', handleChange);
    return () => wide.removeEventListener('change', handleChange);
  }, [open, onClose]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-40">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition-opacity duration-200 ease-out motion-reduce:transition-none data-[closed]:opacity-0"
      />

      {/* `100dvh`, not `100vh`: mobile Safari counts its collapsing address
          bar into the second one, so the last row of a full-height panel sits
          under the browser's own chrome until you scroll. */}
      <DialogPanel
        transition
        className="fixed inset-y-0 left-0 flex h-[100dvh] w-[min(20rem,85vw)] flex-col border-r border-hilevel-border bg-white shadow-xl transition duration-200 ease-out motion-reduce:transition-none data-[closed]:-translate-x-full"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-hilevel-border px-4 py-3">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide text-hilevel-gray">
            Menu
          </DialogTitle>
          <CloseButton onClick={onClose} ariaLabel="Close menu" />
        </div>

        {/* `overscroll-contain` so a flick that reaches the end of the list
            stops there instead of carrying on into the page behind it. */}
        <nav className="flex-1 overflow-y-auto overscroll-contain py-1">
          <Link
            to="/"
            aria-current={pathname === '/' ? 'page' : undefined}
            className={`${HOME} ${pathname === '/' ? MARKED : UNMARKED}`}
          >
            Home
          </Link>

          {sections.map((section) => (
            <Disclosure
              key={section.label}
              as="div"
              className="border-t border-hilevel-border"
              // The drawer is unmounted while shut, so this is read afresh on
              // every open: the section you are already in is the one that is
              // waiting for you, and the other six stay a single screen's
              // worth of rows rather than fifty.
              defaultOpen={section.label === active?.section.label}
            >
              <DisclosureButton className={SECTION_BUTTON}>
                <span className="min-w-0 truncate">{section.label}</span>
                <ChevronDown className="shrink-0 text-xs text-hilevel-gray transition-transform duration-150 group-data-[open]:rotate-180" />
              </DisclosureButton>

              <DisclosurePanel className="pb-1">
                {section.items.map((item) =>
                  isExternal(item.to) ? (
                    <a
                      key={item.to}
                      href={item.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      // Never marked — it is not a route — but still spaced
                      // for the marker, so it lines up with the rows that are.
                      className={`${ITEM} ${UNMARKED}`}
                      // A tab is opened rather than a route entered, so the
                      // pathname never changes and nothing else would shut
                      // the drawer behind it.
                      onClick={onClose}
                    >
                      {item.label} ↗
                    </a>
                  ) : (
                    <Link
                      key={item.to}
                      to={item.to}
                      // There is no hover on a touch screen, so the prefetch
                      // the desktop menu gets on `pointerenter` never fires
                      // here. Pointer-down is the next thing available: it
                      // beats the click by however long the tap lasts, which
                      // is not much but is free.
                      onPointerDown={() => onPrefetchRoute?.(item.to)}
                      onFocus={() => onPrefetchRoute?.(item.to)}
                      // `NavLink` would mark this row and every item whose
                      // route is a prefix of it, so standing on
                      // `/projects/board` lit Projects too. `activeMenuPath`
                      // has already picked the one longest match, and it is
                      // the same one that names the button and decides which
                      // section opens — one answer, given once.
                      aria-current={item === active?.item ? 'page' : undefined}
                      className={`${ITEM} ${item === active?.item ? MARKED : UNMARKED}`}
                    >
                      {item.label}
                    </Link>
                  ),
                )}
              </DisclosurePanel>
            </Disclosure>
          ))}
        </nav>
      </DialogPanel>
    </Dialog>
  );
}
